import type { ExamSection, QuestionType } from '@examflow/core';
import { createSectionConstraint } from '@examflow/core';
import { TCFSection } from './types';
import {
  tcfListeningDefinition,
  tcfLanguageStructureDefinition,
  tcfReadingDefinition,
  tcfWritingDefinition,
} from './questionTypes';

/**
 * TCF 听力理解配置
 */
export const TCF_LISTENING_SECTION: ExamSection = {
  id: TCFSection.LISTENING,
  name: '听力理解 (Compréhension orale)',
  duration: 25, // 25分钟
  /** Official mandatory listening item count (TCF Canada / France written papers). */
  questionCount: 29,
  questionTypes: ['single_choice' as QuestionType],
  questionTypeConstraints: [
    createSectionConstraint(tcfListeningDefinition.id, {
      fieldConstraints: {
        sectionGroup: { required: true },
        questionFormat: { required: true },
      },
    }),
  ],
};

/**
 * TCF 语言结构配置
 */
export const TCF_LANGUAGE_STRUCTURES_SECTION: ExamSection = {
  id: TCFSection.LANGUAGE_STRUCTURES,
  name: '语言结构 (Maîtrise des structures de la langue)',
  duration: 15, // 15分钟
  /** Official mandatory language-structures item count. */
  questionCount: 18,
  questionTypes: ['single_choice' as QuestionType],
  questionTypeConstraints: [
    createSectionConstraint(tcfLanguageStructureDefinition.id, {
      fieldConstraints: {
        subType: { required: true },
      },
    }),
  ],
};

/**
 * TCF 阅读理解配置
 */
export const TCF_READING_SECTION: ExamSection = {
  id: TCFSection.READING,
  name: '阅读理解 (Compréhension écrite)',
  duration: 45, // 45分钟
  /** Official mandatory reading item count. */
  questionCount: 29,
  questionTypes: ['single_choice' as QuestionType],
  questionTypeConstraints: [
    createSectionConstraint(tcfReadingDefinition.id, {
      fieldConstraints: {
        sectionGroup: { required: true },
        materialType: { required: true },
      },
    }),
  ],
};

/**
 * TCF 书面表达（可选）— Expression écrite
 *
 * Included in TCF_SECTIONS so Exam Space / Training / Language Ops can see the Skill.
 * `includeInFullMock: false` keeps mandatory full mock at listening + structures + reading.
 */
export const TCF_WRITING_SECTION: ExamSection = {
  id: TCFSection.WRITING,
  name: '书面表达 (Expression écrite)',
  duration: 60,
  /** Optional paper: three tâches (not part of mandatory TCF full mock). */
  questionCount: 3,
  includeInFullMock: false,
  questionTypes: ['fill_blank' as QuestionType],
  questionTypeConstraints: [
    createSectionConstraint(tcfWritingDefinition.id, {
      fieldConstraints: {
        taskNumber: { required: true },
      },
    }),
  ],
};

/**
 * TCF Skills visible in Exam Space (mandatory three + optional writing).
 * Full mock modules filter via `includeInFullMock !== false`.
 */
export const TCF_SECTIONS: ExamSection[] = [
  TCF_LISTENING_SECTION,
  TCF_LANGUAGE_STRUCTURES_SECTION,
  TCF_READING_SECTION,
  TCF_WRITING_SECTION,
];

/** Mandatory TCF full-mock Skills only. */
export const TCF_MANDATORY_SECTIONS: ExamSection[] = [
  TCF_LISTENING_SECTION,
  TCF_LANGUAGE_STRUCTURES_SECTION,
  TCF_READING_SECTION,
];

/**
 * 获取部分配置
 */
export function getTCFSection(sectionId: TCFSection): ExamSection | undefined {
  return TCF_SECTIONS.find((s) => s.id === sectionId);
}

/**
 * 获取必考部分总考试时长（分钟）— 不含可选书面表达
 */
export function getTotalDuration(): number {
  return TCF_MANDATORY_SECTIONS.reduce((sum, section) => sum + section.duration, 0);
}

/**
 * 获取 Section 的题目类型约束
 * 优先返回新的 questionTypeConstraints，如果没有则返回旧的 questionTypes 转换后的约束
 */
export function getTCFSectionConstraints(
  sectionId: TCFSection
): ReturnType<typeof createSectionConstraint>[] {
  const section = getTCFSection(sectionId);
  if (!section) return [];

  // 如果有新的约束配置，直接返回
  if (section.questionTypeConstraints && section.questionTypeConstraints.length > 0) {
    return section.questionTypeConstraints;
  }

  // 否则从旧的 questionTypes 转换（向后兼容）
  return (
    section.questionTypes?.map((type) =>
      createSectionConstraint(`tcf_${type}`, {
        fieldConstraints: {},
      })
    ) ?? []
  );
}
